import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Moon, Sun, ArrowLeft } from 'lucide-react';
import OccultLogo from './Logo';
import InsideTrackView from './InsideTrack';
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceArea, ReferenceLine, ReferenceDot,
} from 'recharts';

// const BASE_API_URL = 'http://localhost:8080';
const BASE_API_URL = "https://altisonant.xyz/api";

// ── Stations ────────────────────────────────────────────────────────────────
const STATIONS = [
  'NYC', 'MIA', 'AUS', 'MDW', 'LAX', 'DEN', 'DCA', 'ATL',
  'DFW', 'PHL', 'SEA', 'SFO', 'LAS', 'HOU', 'MSY', 'PHX',
] as const;

const STATION_LAT_LON = {
  "KNYC": [40.7829, -73.9654],
  "KMIA": [25.7959, -80.2870],
  "KAUS": [30.1945, -97.6699],
  "KMDW": [41.7868, -87.7522],
  "KLAX": [33.9416, -118.4085],
  "KDEN": [39.8561, -104.6737],
  "KDCA": [38.8521, -77.0377],
  "KATL": [33.6407, -84.4277],
  "KDFW": [32.8998, -97.0403],
  "KPHL": [39.8744, -75.2424],
  "KSEA": [47.4502, -122.3088],
  "KSFO": [37.6213, -122.3790],
  "KLAS": [36.0840, -115.1537],
  "KHOU": [29.6454, -95.2789],
  "KMSY": [29.9934, -90.2580],
  "KPHX": [33.4346, -112.0080],
};

type StationId = (typeof STATIONS)[number];

// ── Metrics ─────────────────────────────────────────────────────────────────
// "wind" is a combined speed+direction chart, not a simple line metric
const SIMPLE_METRICS = [
  'temperature',
  'windGust',
  'barometricPressure',
  'visibility',
  'relativeHumidity',
] as const;
type SimpleMetric = (typeof SIMPLE_METRICS)[number];

// Union of all chart types rendered per card
type ChartSlot = SimpleMetric | 'wind';
const CHART_SLOTS: ChartSlot[] = [
  'temperature',
  'wind',
  'windGust',
  'barometricPressure',
  // 'visibility',
  // 'relativeHumidity',
];

// Labels used by both simple metrics and the wind chart
const CHART_LABELS: Record<ChartSlot, string> = {
  temperature: 'Temperature (°F)',
  wind: 'Wind Speed & Direction',
  windGust: 'Wind Gust (m/s)',
  barometricPressure: 'Pressure (Pa)',
  visibility: 'Visibility (m)',
  relativeHumidity: 'Humidity (%)',
};

// Observation color (red for all charts)
const OBS_COLOR = '#EF4444';

// Per-chart base color for forecast lines
const CHART_FCST_COLORS: Record<ChartSlot, string> = {
  temperature:        '#22C55E', // green
  wind:               '#3B82F6', // blue
  windGust:           '#8B5CF6', // violet
  barometricPressure: '#F59E0B', // amber
  visibility:         '#10B981', // emerald
  relativeHumidity:   '#06B6D4', // cyan
};

// ── Forecast hour → opacity ──────────────────────────────────────────────────
/**
 * Get forecast line opacity for a given forecast_hour.
 * Uses a logarithmic scale: h1 ≈ 0.85, h48 ≈ 0.25.
 * Low hours get high contrast; high hours fade but stay visible.
 */
function getForecastOpacity(forecastHour: number, maxHour: number): number {
  if (maxHour <= 1) return 0.6;
  const normalized = Math.log(forecastHour) / Math.log(maxHour); // 0..1 log-scaled
  return 0.85 - 0.6 * normalized; // 0.85 → 0.25
}

// Keep full metric type for forecast generation & defaults (includes windSpeed/windDirection)
type Metric = SimpleMetric | 'windSpeed' | 'windDirection';


// ── NWS observation shape ───────────────────────────────────────────────────
interface NwsObservation {
  timestamp: string;
  timestamp_ms: number;
  stationId: string;
  stationName: string;
  temperature: number | null;
  windDirection: number | null;
  windSpeed: number | null;
  windGust: number | null;
  barometricPressure: number | null;
  visibility: number | null;
  relativeHumidity: number | null;
  [key: string]: any;
}

// ── Data point used by charts ───────────────────────────────────────────────
// Dynamic keys: obs, fcst_1, fcst_2, fcst_6, etc.
interface DataPoint {
  time: number; // epoch ms
  obs?: number | null;
  [key: string]: number | null | undefined;
}

interface SeriesResult {
  data: DataPoint[];
  forecastHours: number[]; // sorted ascending
}

// ── HRRR forecast shape ─────────────────────────────────────────────────────
interface HrrrForecast {
  forecast_ts: number;
  created_ts: number;
  forecast_hour: number;
  tmp2m: number;
  windSpeed10m: number;
  windDir10m: number;
  gust: number;
  mslma: number;
  vis: number;
  rh2m: number;
  [key: string]: any;
}

// Map our metric names to HRRR fields (with optional transform)
function extractHrrrMetric(f: HrrrForecast, metric: Metric): number | null {
  switch (metric) {
    case 'temperature': return f.tmp2m != null ? +((f.tmp2m - 273.15) * 9 / 5 + 32).toFixed(2) : null;
    case 'windSpeed': return f.windSpeed10m ?? null;
    case 'windDirection': return f.windDir10m ?? null;
    case 'windGust': return f.gust ?? null;
    case 'barometricPressure': return f.mslma ?? null;
    case 'visibility': return f.vis ?? null;
    case 'relativeHumidity': return f.rh2m ?? null;
    default: return null;
  }
}

// ── Extract metric value from an NWS observation ────────────────────────────
function extractMetric(obs: NwsObservation, metric: Metric): number | null {
  const val = obs[metric];
  if (typeof val !== 'number' || isNaN(val)) return null;
  if (metric === 'temperature') return +(val * 9 / 5 + 32).toFixed(2);
  return val;
}

// ── Merge observations + multi-hour forecasts into unified series ────────────
function buildSeries(
  observations: NwsObservation[],
  forecasts: HrrrForecast[],
  metric: Metric,
  windowStartMs: number,
  windowHours: number,
): SeriesResult {
  // Build observation map
  const obsMap = new Map<number, number>();
  observations.forEach(o => {
    const ts = o.timestamp_ms ?? new Date(o.timestamp).getTime();
    const val = extractMetric(o, metric);
    if (val !== null && ts >= windowStartMs) {
      obsMap.set(ts, val);
    }
  });

  // Group forecasts by forecast_hour
  const byHour = new Map<number, Map<number, number>>(); // hour → (ts → value)
  forecasts.forEach(f => {
    const val = extractHrrrMetric(f, metric);
    if (val === null || f.forecast_hour == null) return;
    let hourMap = byHour.get(f.forecast_hour);
    if (!hourMap) { hourMap = new Map(); byHour.set(f.forecast_hour, hourMap); }
    hourMap.set(f.forecast_ts, val);
  });

  const forecastHours = Array.from(byHour.keys()).sort((a, b) => a - b);

  const merged: DataPoint[] = [];
  for (let h = 0; h < windowHours; h++) {
    const t = windowStartMs + h * 3600_000;
    const point: DataPoint = { time: t };

    // Observation
    let obsVal: number | null = null;
    obsMap.forEach((val, ts) => {
      if (obsVal === null && Math.abs(ts - t) < 30 * 60_000) obsVal = val;
    });
    point.obs = obsVal;

    // Each forecast hour
    forecastHours.forEach(fh => {
      const hourMap = byHour.get(fh)!;
      let fcstVal: number | null = null;
      hourMap.forEach((val, ts) => {
        if (fcstVal === null && Math.abs(ts - t) < 30 * 60_000) fcstVal = val;
      });
      point[`fcst_${fh}`] = fcstVal;
    });

    merged.push(point);
  }
  return { data: merged, forecastHours };
}

// ── Wind data point (speed + direction per timestamp, multi-hour forecasts) ──
// Dynamic keys: obsSpeed, obsDir, fcstSpeed_1, fcstDir_1, fcstSpeed_6, fcstDir_6, etc.
interface WindDataPoint {
  time: number;
  obsSpeed: number | null;
  obsDir: number | null;
  [key: string]: number | null | undefined;
}

interface WindSeriesResult {
  data: WindDataPoint[];
  forecastHours: number[];
}

function buildWindSeries(
  observations: NwsObservation[],
  forecasts: HrrrForecast[],
  windowStartMs: number,
  windowHours: number,
): WindSeriesResult {
  // Observation maps
  const speedMap = new Map<number, number>();
  const dirMap = new Map<number, number>();

  observations.forEach(o => {
    const ts = o.timestamp_ms ?? new Date(o.timestamp).getTime();
    if (ts < windowStartMs) return;
    const sp = extractMetric(o, 'windSpeed');
    const dr = extractMetric(o, 'windDirection');
    if (sp !== null) speedMap.set(ts, sp);
    if (dr !== null) dirMap.set(ts, dr);
  });

  // Group forecast speed+dir by forecast_hour
  const byHourSpeed = new Map<number, Map<number, number>>();
  const byHourDir = new Map<number, Map<number, number>>();
  forecasts.forEach(f => {
    if (f.forecast_hour == null) return;
    if (f.windSpeed10m != null) {
      let m = byHourSpeed.get(f.forecast_hour);
      if (!m) { m = new Map(); byHourSpeed.set(f.forecast_hour, m); }
      m.set(f.forecast_ts, f.windSpeed10m);
    }
    if (f.windDir10m != null) {
      let m = byHourDir.get(f.forecast_hour);
      if (!m) { m = new Map(); byHourDir.set(f.forecast_hour, m); }
      m.set(f.forecast_ts, f.windDir10m);
    }
  });

  const hourSet = new Set<number>();
  byHourSpeed.forEach((_, k) => hourSet.add(k));
  byHourDir.forEach((_, k) => hourSet.add(k));
  const forecastHours = Array.from(hourSet).sort((a, b) => a - b);

  const merged: WindDataPoint[] = [];
  for (let h = 0; h < windowHours; h++) {
    const t = windowStartMs + h * 3600_000;
    const point: WindDataPoint = { time: t, obsSpeed: null, obsDir: null };

    speedMap.forEach((val, ts) => { if (point.obsSpeed === null && Math.abs(ts - t) < 30 * 60_000) point.obsSpeed = val; });
    dirMap.forEach((val, ts) => { if (point.obsDir === null && Math.abs(ts - t) < 30 * 60_000) point.obsDir = val; });

    forecastHours.forEach(fh => {
      const sKey = `fcstSpeed_${fh}`;
      const dKey = `fcstDir_${fh}`;
      point[sKey] = null;
      point[dKey] = null;
      const sm = byHourSpeed.get(fh);
      if (sm) sm.forEach((val, ts) => { if (point[sKey] === null && Math.abs(ts - t) < 30 * 60_000) point[sKey] = val; });
      const dm = byHourDir.get(fh);
      if (dm) dm.forEach((val, ts) => { if (point[dKey] === null && Math.abs(ts - t) < 30 * 60_000) point[dKey] = val; });
    });

    merged.push(point);
  }
  return { data: merged, forecastHours };
}

// ── Wind arrow custom dot ───────────────────────────────────────────────────
const WindArrow: React.FC<{
  cx?: number; cy?: number;
  payload?: WindDataPoint;
  speedKey: string;  // e.g. 'obsSpeed' or 'fcstSpeed_3'
  dirKey: string;    // e.g. 'obsDir' or 'fcstDir_3'
  color: string;
}> = ({ cx, cy, payload, speedKey, dirKey, color }) => {
  if (cx == null || cy == null || !payload) return null;
  const speed = payload[speedKey];
  if (speed == null) return null;
  const dir = payload[dirKey];
  const rotation = (typeof dir === 'number') ? dir : 0;
  return (
    <g transform={`translate(${cx},${cy}) rotate(${rotation})`}>
      <path
        d="M 0,-5 L 3,4 L 0,2 L -3,4 Z"
        fill={color}
        stroke={color}
        strokeWidth={0.5}
      />
    </g>
  );
};

// ── Shared helpers ──────────────────────────────────────────────────────────
const CHART_HEIGHT = 70;

function formatTime(ts: number) {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:00`;
}

function formatTickDate(ts: number) {
  const d = new Date(ts);
  const day = d.getDate();
  const hr = d.getHours();
  if (hr === 0) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${day}`;
  }
  return `${hr}:00`;
}

const TOOLTIP_STYLE = {
  fontSize: 10,
  backgroundColor: 'rgba(30,30,30,0.9)',
  border: 'none',
  borderRadius: 4,
  color: '#fff',
  padding: '4px 6px',
};

// Day-shading fill: light mode vs dark mode is handled by opacity
const DAY_SHADE_FILL = '#8884';

function DayShading({ windowStartMs }: { windowStartMs: number }) {
  // Shade the second day (hours 24-48)
  const day2Start = windowStartMs + 24 * 3600_000;
  const day2End = windowStartMs + 48 * 3600_000;
  return (
    <ReferenceArea
      x1={day2Start}
      x2={day2End}
      fill={DAY_SHADE_FILL}
      fillOpacity={0.15}
      ifOverflow="hidden"
    />
  );
}

// ── WindChart component ─────────────────────────────────────────────────────
interface WindChartProps {
  data: WindDataPoint[];
  forecastHours: number[];
  showXAxis: boolean;
  windowStartMs: number;
  chartHeight?: number;
}

const WindChart: React.FC<WindChartProps> = ({ data, forecastHours, showXAxis, windowStartMs, chartHeight = CHART_HEIGHT }) => {
  const fcstColor = CHART_FCST_COLORS.wind;
  const maxHour = forecastHours.length > 0 ? Math.max(...forecastHours) : 1;
  const nowMs = Date.now();

  const dirToCardinal = (deg: number | null | undefined) => {
    if (deg == null) return '?';
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
  };

  return (
    <div>
      <div className="text-[9px] text-gray-500 dark:text-gray-400 font-medium pl-1 leading-tight">
        {CHART_LABELS.wind}
      </div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <DayShading windowStartMs={windowStartMs} />
          <XAxis
            dataKey="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTickDate}
            tick={showXAxis ? { fontSize: 8 } : false}
            axisLine={showXAxis}
            tickLine={showXAxis}
            height={showXAxis ? 16 : 0}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 8 }}
            width={32}
            tickFormatter={(v: number) => v.toFixed(0)}
          />
          <Tooltip
            labelFormatter={formatTime}
            formatter={(value: number, name: string, props: any) => {
              const p = props.payload as WindDataPoint;
              let dir: number | null | undefined = null;
              if (name === 'Obs') {
                dir = p.obsDir;
              } else {
                const m = name.match(/h(\d+)/);
                if (m) dir = p[`fcstDir_${m[1]}`] as number | null;
              }
              return [`${value?.toFixed(1)} m/s ${dirToCardinal(dir)}`, name];
            }}
            contentStyle={TOOLTIP_STYLE}
          />
          <ReferenceLine x={nowMs} stroke="#888" strokeDasharray="3 3" strokeWidth={1} />
          {/* Forecast lines (render first so obs draws on top) */}
          {forecastHours.map(fh => {
            const opacity = getForecastOpacity(fh, maxHour);
            const speedKey = `fcstSpeed_${fh}`;
            const dirKey = `fcstDir_${fh}`;
            return (
              <Line
                key={fh}
                dataKey={speedKey}
                stroke={fcstColor}
                strokeWidth={1}
                strokeDasharray="4 3"
                strokeOpacity={opacity}
                dot={(props: any) => <WindArrow key={props.index} {...props} speedKey={speedKey} dirKey={dirKey} color={fcstColor} />}
                connectNulls
                isAnimationActive={false}
                name={`Fcst h${fh}`}
              />
            );
          })}
          <Line
            dataKey="obsSpeed"
            stroke={OBS_COLOR}
            strokeWidth={1.5}
            dot={(props: any) => <WindArrow key={props.index} {...props} speedKey="obsSpeed" dirKey="obsDir" color={OBS_COLOR} />}
            connectNulls={false}
            isAnimationActive={false}
            name="Obs"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Temperature daily high/low ───────────────────────────────────────────────
interface DayExtremes {
  dayLabel: string;
  highObs: { time: number; value: number } | null;
  lowObs: { time: number; value: number } | null;
  highFcst: { time: number; value: number } | null;
  lowFcst: { time: number; value: number } | null;
}

function computeTempExtremes(data: DataPoint[], windowStartMs: number, forecastHours: number[]): DayExtremes[] {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const result: DayExtremes[] = [];

  for (let d = 0; d < 2; d++) {
    const dayStart = windowStartMs + d * 86400_000;
    const dayEnd = dayStart + 86400_000;
    const dt = new Date(dayStart);
    const dayLabel = `${days[dt.getDay()]} ${months[dt.getMonth()]} ${dt.getDate()}`;

    let highObs: { time: number; value: number } | null = null;
    let lowObs: { time: number; value: number } | null = null;
    let highFcst: { time: number; value: number } | null = null;
    let lowFcst: { time: number; value: number } | null = null;

    for (const pt of data) {
      if (pt.time < dayStart || pt.time >= dayEnd) continue;

      // Observations
      if (pt.obs != null) {
        if (highObs === null || pt.obs > highObs.value) highObs = { time: pt.time, value: pt.obs };
        if (lowObs === null || pt.obs < lowObs.value) lowObs = { time: pt.time, value: pt.obs };
      }

      // Forecast: use the lowest forecast_hour (most recent/accurate) value at each time
      for (const fh of forecastHours) {
        const val = pt[`fcst_${fh}`];
        if (val != null) {
          if (highFcst === null || val > highFcst.value) highFcst = { time: pt.time, value: val };
          if (lowFcst === null || val < lowFcst.value) lowFcst = { time: pt.time, value: val };
        }
      }
    }

    result.push({ dayLabel, highObs, lowObs, highFcst, lowFcst });
  }

  return result;
}

// Triangle marker for high/low extremes on temperature chart
const ExtremeTriangle: React.FC<{ cx?: number; cy?: number; type: 'high' | 'low' }> = ({ cx, cy, type }) => {
  if (cx == null || cy == null) return null;
  const color = type === 'high' ? '#EF4444' : '#3B82F6';
  const path = type === 'high'
    ? `M ${cx},${cy - 6} L ${cx - 5},${cy + 3} L ${cx + 5},${cy + 3} Z`   // pointing up
    : `M ${cx},${cy + 6} L ${cx - 5},${cy - 3} L ${cx + 5},${cy - 3} Z`;  // pointing down
  return <path d={path} fill={color} stroke={color} strokeWidth={0.5} />;
};

// ── MetricChart component ───────────────────────────────────────────────────
interface MetricChartProps {
  metric: SimpleMetric;
  data: DataPoint[];
  forecastHours: number[];
  showXAxis: boolean;
  windowStartMs: number;
  chartHeight?: number;
  tempExtremes?: DayExtremes[];
}

const MetricChart: React.FC<MetricChartProps> = ({ metric, data, forecastHours, showXAxis, windowStartMs, chartHeight = CHART_HEIGHT, tempExtremes }) => {
  const fcstColor = CHART_FCST_COLORS[metric];
  const maxHour = forecastHours.length > 0 ? Math.max(...forecastHours) : 1;
  const nowMs = Date.now();

  // Collect forecast extreme markers for temperature chart
  const extremeMarkers: { time: number; value: number; type: 'high' | 'low' }[] = [];
  if (tempExtremes) {
    for (const day of tempExtremes) {
      if (day.highFcst) extremeMarkers.push({ ...day.highFcst, type: 'high' });
      if (day.lowFcst) extremeMarkers.push({ ...day.lowFcst, type: 'low' });
    }
  }

  return (
    <div>
      <div className="text-[9px] text-gray-500 dark:text-gray-400 font-medium pl-1 leading-tight">
        {CHART_LABELS[metric]}
      </div>
      {tempExtremes && (
        <div className="flex gap-3 pl-1 mb-0.5 flex-wrap">
          {tempExtremes.map(day => (
            <div key={day.dayLabel} className="text-[9px] leading-tight">
              <span className="text-gray-500 dark:text-gray-400 font-medium">{day.dayLabel}: </span>
              {day.highFcst && (
                <span className="text-red-500">H̄ {day.highFcst.value.toFixed(1)}° </span>
              )}
              {day.highObs && (
                <span className="text-red-700 dark:text-red-400 font-semibold">H {day.highObs.value.toFixed(1)}° </span>
              )}
              {day.lowFcst && (
                <span className="text-blue-500">L̄ {day.lowFcst.value.toFixed(1)}° </span>
              )}
              {day.lowObs && (
                <span className="text-blue-700 dark:text-blue-400 font-semibold">L {day.lowObs.value.toFixed(1)}°</span>
              )}
            </div>
          ))}
        </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={data} margin={{ top: 2, right: 4, bottom: 0, left: 0 }}>
          <DayShading windowStartMs={windowStartMs} />
          <XAxis
            dataKey="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTickDate}
            tick={showXAxis ? { fontSize: 8 } : false}
            axisLine={showXAxis}
            tickLine={showXAxis}
            height={showXAxis ? 16 : 0}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 8 }}
            width={32}
            tickFormatter={(v: number) => {
              if (Math.abs(v) >= 10000) return `${(v / 1000).toFixed(0)}k`;
              if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k`;
              return v.toFixed(1);
            }}
          />
          <Tooltip
            labelFormatter={formatTime}
            formatter={(value: number, name: string) => [value?.toFixed(2) ?? 'N/A', name]}
            contentStyle={TOOLTIP_STYLE}
          />
          <ReferenceLine x={nowMs} stroke="#888" strokeDasharray="3 3" strokeWidth={1} />
          {/* Forecast lines (render first so obs draws on top) */}
          {forecastHours.map(fh => {
            const opacity = getForecastOpacity(fh, maxHour);
            return (
              <Line
                key={fh}
                dataKey={`fcst_${fh}`}
                stroke={fcstColor}
                strokeWidth={1}
                strokeDasharray="4 3"
                strokeOpacity={opacity}
                dot={{ r: 1.5, fill: fcstColor, fillOpacity: opacity, stroke: 'none' }}
                connectNulls
                isAnimationActive={false}
                name={`Fcst h${fh}`}
              />
            );
          })}
          <Line
            dataKey="obs"
            stroke={OBS_COLOR}
            strokeWidth={1.5}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
            name="Obs"
          />
          {/* Temperature extreme markers */}
          {extremeMarkers.map((m, i) => (
            <ReferenceDot
              key={`ext-${i}`}
              x={m.time}
              y={m.value}
              shape={(props: any) => <ExtremeTriangle cx={props.cx} cy={props.cy} type={m.type} />}
              ifOverflow="extendDomain"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── StationCard component ───────────────────────────────────────────────────
interface StationCardProps {
  stationId: StationId;
  date: string;
  onClick?: () => void;
}

interface StationState {
  observations: NwsObservation[];
  forecasts: HrrrForecast[];
  stationName: string | null;
  loading: boolean;
  error: string | null;
}

const StationCard: React.FC<StationCardProps> = ({ stationId, date, onClick }) => {
  const [state, setState] = useState<StationState>({
    observations: [],
    forecasts: [],
    stationName: null,
    loading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const stationKey = `K${stationId}` as keyof typeof STATION_LAT_LON;
      const latLon = STATION_LAT_LON[stationKey];

      // Fetch observations and forecast in parallel
      const [obsRes, fcstRes] = await Promise.all([
        fetch(`${BASE_API_URL}/api/nws_report?station=K${stationId}&date=${date}`),
        latLon
          ? fetch(`${BASE_API_URL}/api/forecast_for?date=${date}&lat=${latLon[0]}&lon=${latLon[1]}`)
          : Promise.resolve(null),
      ]);

      if (!obsRes.ok) throw new Error(`Obs HTTP ${obsRes.status}`);
      const obsJson = await obsRes.json();
      const observations: NwsObservation[] = Array.isArray(obsJson)
        ? obsJson
        : obsJson.observations ?? [];

      let forecasts: HrrrForecast[] = [];
      if (fcstRes && fcstRes.ok) {
        const fcstJson = await fcstRes.json();
        forecasts = Array.isArray(fcstJson) ? fcstJson : [];
      }

      const name = observations[0]?.stationName ?? stationId;

      setState({ observations, forecasts, stationName: name, loading: false, error: null });
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Fetch failed',
      }));
    }
  }, [stationId, date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 48h window starting at midnight of the selected date
  const windowStart = new Date(`${date}T00:00:00`).getTime();
  const windowHours = 48;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-2 overflow-hidden${onClick ? ' cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-shadow' : ''}`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-1">
        <div>
          <h3 className="text-sm font-semibold">K{stationId}</h3>
          {state.stationName && state.stationName !== stationId && (
            <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{state.stationName}</div>
          )}
        </div>
        {state.loading && (
          <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {state.error && (
        <div className="text-xs text-red-500 mb-2">
          Error: {state.error}
          <button onClick={fetchData} className="ml-2 text-indigo-500 hover:text-indigo-700">
            Retry
          </button>
        </div>
      )}

      {/* Metric charts */}
      {CHART_SLOTS.map((slot, idx) => {
        const isLast = idx === CHART_SLOTS.length - 1;
        if (slot === 'wind') {
          const { data: windData, forecastHours } = buildWindSeries(state.observations, state.forecasts, windowStart, windowHours);
          return <WindChart key={slot} data={windData} forecastHours={forecastHours} showXAxis={isLast} windowStartMs={windowStart} />;
        }
        const { data, forecastHours } = buildSeries(state.observations, state.forecasts, slot, windowStart, windowHours);
        const extras = slot === 'temperature' ? computeTempExtremes(data, windowStart, forecastHours) : undefined;
        return <MetricChart key={slot} metric={slot} data={data} forecastHours={forecastHours} showXAxis={isLast} windowStartMs={windowStart} tempExtremes={extras} />;
      })}
    </div>
  );
};

// ── Mock Kalshi data ─────────────────────────────────────────────────────────
function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

interface KalshiContract {
  day: string;       // e.g. "Sat Feb 22"
  label: string;     // e.g. "High ≥ 55°F"
  kalshiPrice: number;
  ourProb: number;
}

function generateMockKalshi(stationId: StationId, date: string): KalshiContract[] {
  const baseDate = new Date(`${date}T00:00:00`);
  const contracts: KalshiContract[] = [];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  for (let d = 0; d < 3; d++) {
    const dt = new Date(baseDate.getTime() + d * 86400_000);
    const dayLabel = `${days[dt.getDay()]} ${months[dt.getMonth()]} ${dt.getDate()}`;
    const seed = hashCode(`${stationId}-${d}-${date}`);

    const highThresh = 40 + (seed % 40);         // 40–79°F
    const kalshiHigh = 0.3 + ((seed % 50) / 100);  // 0.30–0.79
    const ourHigh = kalshiHigh + ((((seed >> 4) % 21) - 10) / 100); // ±10%

    const lowThresh = 20 + ((seed >> 3) % 25);     // 20–44°F
    const kalshiLow = 0.2 + (((seed >> 2) % 55) / 100); // 0.20–0.74
    const ourLow = kalshiLow + ((((seed >> 5) % 21) - 10) / 100);   // ±10%

    contracts.push({
      day: dayLabel,
      label: `High ≥ ${highThresh}°F`,
      kalshiPrice: Math.round(kalshiHigh * 100) / 100,
      ourProb: Math.round(Math.max(0, Math.min(1, ourHigh)) * 100) / 100,
    });
    contracts.push({
      day: dayLabel,
      label: `Low ≤ ${lowThresh}°F`,
      kalshiPrice: Math.round(kalshiLow * 100) / 100,
      ourProb: Math.round(Math.max(0, Math.min(1, ourLow)) * 100) / 100,
    });
  }
  return contracts;
}

// ── KalshiPanel component ────────────────────────────────────────────────────
const KalshiPanel: React.FC<{ stationId: StationId; date: string }> = ({ stationId, date }) => {
  const contracts = useMemo(() => generateMockKalshi(stationId, date), [stationId, date]);

  // Group by day
  const days: { day: string; rows: KalshiContract[] }[] = [];
  contracts.forEach(c => {
    const last = days[days.length - 1];
    if (last && last.day === c.day) {
      last.rows.push(c);
    } else {
      days.push({ day: c.day, rows: [c] });
    }
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <h3 className="text-sm font-semibold mb-3">Kalshi Markets</h3>
      <div className="space-y-3">
        {days.map(({ day, rows }) => (
          <div key={day}>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{day}</div>
            <div className="space-y-1">
              {rows.map((row, i) => {
                const edge = row.ourProb - row.kalshiPrice;
                const edgeColor = edge >= 0 ? 'text-green-500' : 'text-red-500';
                return (
                  <div key={i} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700/50 rounded px-2 py-1.5">
                    <span className="font-medium w-28 truncate">{row.label}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      Kalshi <span className="font-mono">${row.kalshiPrice.toFixed(2)}</span>
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      Ours <span className="font-mono">{(row.ourProb * 100).toFixed(0)}%</span>
                    </span>
                    <span className={`font-mono font-semibold ${edgeColor}`}>
                      {edge >= 0 ? '+' : ''}{(edge * 100).toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── StationDetail component ──────────────────────────────────────────────────
interface StationDetailProps {
  stationId: StationId;
  date: string;
  onBack: () => void;
}

const StationDetail: React.FC<StationDetailProps> = ({ stationId, date, onBack }) => {
  const [state, setState] = useState<StationState>({
    observations: [],
    forecasts: [],
    stationName: null,
    loading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const stationKey = `K${stationId}` as keyof typeof STATION_LAT_LON;
      const latLon = STATION_LAT_LON[stationKey];

      const [obsRes, fcstRes] = await Promise.all([
        fetch(`${BASE_API_URL}/api/nws_report?station=K${stationId}&date=${date}`),
        latLon
          ? fetch(`${BASE_API_URL}/api/forecast_for?date=${date}&lat=${latLon[0]}&lon=${latLon[1]}`)
          : Promise.resolve(null),
      ]);

      if (!obsRes.ok) throw new Error(`Obs HTTP ${obsRes.status}`);
      const obsJson = await obsRes.json();
      const observations: NwsObservation[] = Array.isArray(obsJson)
        ? obsJson
        : obsJson.observations ?? [];

      let forecasts: HrrrForecast[] = [];
      if (fcstRes && fcstRes.ok) {
        const fcstJson = await fcstRes.json();
        forecasts = Array.isArray(fcstJson) ? fcstJson : [];
      }

      const name = observations[0]?.stationName ?? stationId;
      setState({ observations, forecasts, stationName: name, loading: false, error: null });
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Fetch failed',
      }));
    }
  }, [stationId, date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const windowStart = new Date(`${date}T00:00:00`).getTime();
  const windowHours = 48;
  const detailChartHeight = 120;

  // Pre-compute temperature extremes for chart markers + summary panel
  const tempSeries = useMemo(
    () => buildSeries(state.observations, state.forecasts, 'temperature', windowStart, windowHours),
    [state.observations, state.forecasts, windowStart],
  );
  const tempExtremes = useMemo(
    () => computeTempExtremes(tempSeries.data, windowStart, tempSeries.forecastHours),
    [tempSeries, windowStart],
  );

  return (
    <div>
      {/* Back button + header */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 mb-3"
      >
        <ArrowLeft size={16} />
        All Stations
      </button>

      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-bold">K{stationId}</h2>
        {state.stationName && state.stationName !== stationId && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{state.stationName}</span>
        )}
        {state.loading && (
          <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {state.error && (
        <div className="text-xs text-red-500 mb-3">
          Error: {state.error}
          <button onClick={fetchData} className="ml-2 text-indigo-500 hover:text-indigo-700">Retry</button>
        </div>
      )}

      {/* Temperature Extremes Summary */}
      {tempExtremes.some(d => d.highFcst || d.lowFcst || d.highObs || d.lowObs) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <h3 className="text-sm font-semibold mb-2">Temperature Extremes (°F)</h3>
          <div className="grid grid-cols-2 gap-4">
            {tempExtremes.map(day => (
              <div key={day.dayLabel}>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{day.dayLabel}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">Forecast</div>
                    {day.highFcst && (
                      <div className="text-red-500 font-medium">High: {day.highFcst.value.toFixed(1)}°</div>
                    )}
                    {day.lowFcst && (
                      <div className="text-blue-500 font-medium">Low: {day.lowFcst.value.toFixed(1)}°</div>
                    )}
                    {!day.highFcst && !day.lowFcst && (
                      <div className="text-gray-400">No data</div>
                    )}
                  </div>
                  <div>
                    <div className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">Observed</div>
                    {day.highObs && (
                      <div className="text-red-700 dark:text-red-400 font-medium">High: {day.highObs.value.toFixed(1)}°</div>
                    )}
                    {day.lowObs && (
                      <div className="text-blue-700 dark:text-blue-400 font-medium">Low: {day.lowObs.value.toFixed(1)}°</div>
                    )}
                    {!day.highObs && !day.lowObs && (
                      <div className="text-gray-400">Pending</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two-column layout: charts left, Kalshi right */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-3/5">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3">
            {CHART_SLOTS.map((slot, idx) => {
              const isLast = idx === CHART_SLOTS.length - 1;
              if (slot === 'wind') {
                const { data: windData, forecastHours } = buildWindSeries(state.observations, state.forecasts, windowStart, windowHours);
                return <WindChart key={slot} data={windData} forecastHours={forecastHours} showXAxis={isLast} windowStartMs={windowStart} chartHeight={detailChartHeight} />;
              }
              if (slot === 'temperature') {
                return <MetricChart key={slot} metric={slot} data={tempSeries.data} forecastHours={tempSeries.forecastHours} showXAxis={isLast} windowStartMs={windowStart} chartHeight={detailChartHeight} tempExtremes={tempExtremes} />;
              }
              const { data, forecastHours } = buildSeries(state.observations, state.forecasts, slot, windowStart, windowHours);
              return <MetricChart key={slot} metric={slot} data={data} forecastHours={forecastHours} showXAxis={isLast} windowStartMs={windowStart} chartHeight={detailChartHeight} />;
            })}
          </div>
        </div>
        <div className="lg:w-2/5">
          <KalshiPanel stationId={stationId} date={date} />
        </div>
      </div>
    </div>
  );
};

// ── Main App ────────────────────────────────────────────────────────────────
const AltisonantApp: React.FC = () => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return saved === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [currentDate, setCurrentDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const [activeView, setActiveView] = useState<'insider' | 'weather'>('insider');
  const [selectedStation, setSelectedStation] = useState<StationId | null>(null);

  const switchView = (view: 'insider' | 'weather') => {
    setActiveView(view);
    setSelectedStation(null);
  };

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('darkMode', next.toString());
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo + title */}
            <div className="flex items-center flex-shrink-0">
              <OccultLogo darkMode={darkMode} />
              <h1 className="text-2xl px-3 font-semibold font-sans">ALTISONANT</h1>
            </div>

            {/* Tab navigation */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button
                onClick={() => switchView('insider')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'insider'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Inside Track
              </button>
              <button
                onClick={() => switchView('weather')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'weather'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Weather
              </button>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {activeView === 'weather' && (
                <input
                  type="date"
                  value={currentDate}
                  onChange={e => setCurrentDate(e.target.value)}
                  className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              )}
              <button
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                onClick={toggleDarkMode}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard */}
      <main className="container mx-auto px-4 py-6">
        {activeView === 'insider' ? (
          <InsideTrackView />
        ) : selectedStation ? (
          <StationDetail
            stationId={selectedStation}
            date={currentDate}
            onBack={() => setSelectedStation(null)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {STATIONS.map(id => (
              <StationCard key={id} stationId={id} date={currentDate} onClick={() => setSelectedStation(id)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AltisonantApp;
